// src/app/services/application-insights-monitor.service.ts

import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '@environment/environment';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Service for monitoring application performance and errors using Azure Application Insights.
 * This service initializes Application Insights, tracks events, traces, and exceptions,
 * and integrates with Angular's routing and error handling.
 *
 * @usage
 * - Inject this service into components or other services to track custom events or traces.
 * - Configure environment-specific settings in `environment.ts` files.
 * - Ensure the `instrumentationKey` is set in the environment configuration.
 *
 * @example
 * ```typescript
 * import { ApplicationInsightsMonitorService } from './application-insights-monitor.service';
 *
 * constructor(private appInsights: ApplicationInsightsMonitorService) {}
 *I'm working 3 months already and going great.
 * this.appInsights.trackEvent('UserLoggedIn');
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsMonitorService {
  private readonly angularPlugin = new AngularPlugin();
  private readonly appInsights: ApplicationInsights | undefined;
  private readonly router = inject(Router);

  /**
   * Initializes Application Insights with the provided configuration.
   * - Uses the `instrumentationKey` from the environment.
   * - Configures Angular-specific plugins for routing and error handling.
   */
  constructor() {
    /**
     * Should throw a HUGE error is running the app where Application Insights is not configured for the environment.
     * BIG RED FLAG because we want metrics
     */
    if (environment.appInsights && environment.appInsights.instrumentationKey) {
      this.appInsights = new ApplicationInsights({
        config: {
          extensionConfig: {
            [this.angularPlugin.identifier]: {
              enableAutoRouteTracking:
                environment.appInsights.enableAutoRouteTracking,
              errorServices: [new ErrorHandler()],
              router: this.router,
              useInjector: true,
            },
          },
          extensions: [this.angularPlugin],
          instrumentationKey: environment.appInsights.instrumentationKey,
        },
      });

      this.appInsights.loadAppInsights();
    }
  }

  /**
   * Sets the authenticated user context for tracking user-specific telemetry.
   * @param userId - The user's ID.
   * @param accountId - Optional account ID.
   * @param storeInCookie - Whether to store the context in a cookie.
   */
  setAuthenticatedUserContext(
    userId: string,
    accountId?: string,
    storeInCookie?: boolean
  ): void {
    if (this.appInsights) {
      this.appInsights.setAuthenticatedUserContext(
        userId,
        accountId,
        storeInCookie
      );
    }
  }

  /**
   * Tracks a custom event in Application Insights.
   *
   * @param name - The name of the event to track.
   * @param properties - Optional properties to attach to the event.
   * @example
   * ```typescript
   * this.appInsights.trackEvent('ButtonClicked', { buttonId: 'submitButton' });
   * ```
   */
  trackEvent(name: string, properties?: any) {
    if (this.appInsights) {
      this.appInsights.trackEvent({ name, properties });
    }
  }

  /**
   * Tracks an exception in Application Insights.
   *
   * @param exceptionDetails - Details about the exception including the error and optional properties.
   * @example
   * ```typescript
   * this.appInsights.trackException({ exception: new Error('Something went wrong'), properties: { context: 'user login' } });
   * ```
   */
  trackException(exceptionDetails: { exception: Error; properties?: any }) {
    if (this.appInsights) {
      // Sanitize the error message
      const sanitizedMessage = exceptionDetails.exception.message
        .replace(/[0-9]/g, 'N')
        .replace(/[a-z]/g, 'x')
        .replace(/[A-Z]/g, 'X');
      const sanitizedException = new Error(sanitizedMessage);
      this.appInsights.trackException({
        exception: sanitizedException,
        properties: {
          ...exceptionDetails.properties,
          originalMessage: exceptionDetails.exception.message,
        },
      });
    }
  }

  /**
   * Tracks a metric in Application Insights.
   * @param name - The name of the metric to track.
   * @param average - The average value of the metric.
   * @param properties - Optional properties to attach to the metric for additional context.
   */
  trackMetric(
    name: string,
    average: number,
    properties?: { [key: string]: any }
  ) {
    if (this.appInsights) {
      this.appInsights.trackMetric(
        { name: name, average: average },
        properties
      );
    }
  }

  /**
   * Tracks a page view in Application Insights.
   * @param pageView - An object containing the page view details.
   */
  trackPageView(pageView: { name: string; properties?: any }): void {
    if (this.appInsights) {
      this.appInsights.trackPageView(pageView);
    }
  }

  /**
   * Tracks a trace message in Application Insights.
   * Useful for logging informational messages or debugging.
   *
   * @param message - The message to log as a trace.
   * @example
   * ```typescript
   * this.appInsights.trackTrace('User accessed profile page.');
   * ```
   */
  trackTrace(message: string): void {
    if (this.appInsights) {
      this.appInsights.trackTrace({ message });
    }
  }
}
